import React, { useState, useRef, useEffect } from 'react';
import { MessageSquarePlus, X, Loader2, Camera, Check, Bug, Lightbulb, MessageCircle, Heart, CreditCard, Compass } from 'lucide-react';
import { getAuthHeaders } from '../context/AuthContext';

import { BACKEND_URL } from '../config/api';

const FEEDBACK_TYPES = [
  { value: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-500 bg-red-50 border-red-200', activeColor: 'bg-red-100 border-red-400' },
  { value: 'feature', label: 'Feature Idea', icon: Lightbulb, color: 'text-amber-500 bg-amber-50 border-amber-200', activeColor: 'bg-amber-100 border-amber-400' },
  { value: 'billing', label: 'Billing', icon: CreditCard, color: 'text-green-500 bg-green-50 border-green-200', activeColor: 'bg-green-100 border-green-400' },
  { value: 'usability', label: 'Usability', icon: Compass, color: 'text-indigo-500 bg-indigo-50 border-indigo-200', activeColor: 'bg-indigo-100 border-indigo-400' },
  { value: 'general', label: 'General', icon: MessageCircle, color: 'text-blue-500 bg-blue-50 border-blue-200', activeColor: 'bg-blue-100 border-blue-400' },
  { value: 'praise', label: 'Love It!', icon: Heart, color: 'text-pink-500 bg-pink-50 border-pink-200', activeColor: 'bg-pink-100 border-pink-400' },
];

const FeedbackWidget = ({ isOpen, onClose, initialType = null }) => {
  const [feedbackType, setFeedbackType] = useState('general');
  const [message, setMessage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const resetForm = () => {
    setFeedbackType('general');
    setMessage('');
    setImageFile(null);
    setImagePreview(null);
    setSubmitted(false);
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetForm, 300);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('feedback_type', feedbackType);
      formData.append('message', message.trim());
      formData.append('page_context', window.location.pathname);
      if (imageFile) {
        formData.append('file', imageFile);
      }

      const res = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (res.ok) {
        setSubmitted(true);
        setTimeout(handleClose, 2000);
      } else {
        const cloned = res.clone();
        try {
          const err = await cloned.json();
          alert(err.detail || 'Failed to submit feedback');
        } catch {
          alert('Failed to submit feedback. Please try again.');
        }
      }
    } catch (err) {
      console.error('Feedback submit error:', err);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Set initial type when opening with a specific type
  React.useEffect(() => {
    if (isOpen && initialType) {
      setFeedbackType(initialType);
    }
  }, [isOpen, initialType]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-charcoal/60 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-xl shadow-2xl animate-in slide-in-from-bottom duration-200 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-5 border-b border-beige flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-medium text-charcoal">Send Feedback</h2>
                <p className="text-xs text-gray-soft mt-0.5">Help us make Kura Stash better</p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-gray-soft hover:text-charcoal rounded-lg hover:bg-cream transition-colors"
                data-testid="feedback-close-btn"
              >
                <X size={18} />
              </button>
            </div>

            {/* Success State */}
            {submitted ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-charcoal mb-1">Thank you!</h3>
                <p className="text-sm text-gray-soft">Your feedback helps us build a better Kura Stash.</p>
              </div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="p-5 space-y-4 overflow-y-auto flex-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {/* Type selector */}
                  <div>
                    <label className="block text-xs font-medium text-charcoal mb-2 uppercase tracking-wide">What kind of feedback?</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {FEEDBACK_TYPES.map((type) => {
                        const Icon = type.icon;
                        const isActive = feedbackType === type.value;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setFeedbackType(type.value)}
                            className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all duration-150 text-left ${
                              isActive ? type.activeColor : `${type.color} hover:opacity-80`
                            }`}
                            data-testid={`feedback-type-${type.value}`}
                          >
                            <Icon size={16} />
                            <span className="text-sm font-medium">{type.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-medium text-charcoal mb-2 uppercase tracking-wide">
                      {feedbackType === 'bug' ? 'What happened?' : feedbackType === 'feature' ? 'Describe your idea' : 'Your message'}
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      required
                      placeholder={
                        feedbackType === 'bug'
                          ? "I was trying to... and then..."
                          : feedbackType === 'feature'
                          ? "It would be great if..."
                          : feedbackType === 'praise'
                          ? "I love how..."
                          : feedbackType === 'billing'
                          ? "Regarding my subscription or payment..."
                          : feedbackType === 'usability'
                          ? "I found it confusing when..."
                          : "Tell us what's on your mind..."
                      }
                      className="w-full px-4 py-3 border border-beige rounded-lg text-sm focus:border-sage focus:ring-2 focus:ring-sage/20 transition-all resize-none"
                      data-testid="feedback-message"
                    />
                  </div>

                  {/* Image upload */}
                  <div>
                    <label className="block text-xs font-medium text-charcoal mb-2 uppercase tracking-wide">
                      Attach a screenshot <span className="text-gray-light font-normal normal-case">(optional)</span>
                    </label>
                    <div
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
                      onDrop={(e) => {
                        e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file && file.type.startsWith('image/')) {
                          setImageFile(file);
                          setImagePreview(URL.createObjectURL(file));
                        }
                      }}
                    >
                    {imagePreview ? (
                      <div className="relative inline-block">
                        <img src={imagePreview} alt="Attached" className="max-h-32 rounded-lg border border-beige" />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600"
                          data-testid="feedback-remove-image"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex items-center gap-2 w-full px-4 py-4 border-2 border-dashed rounded-lg text-sm transition-colors ${
                          isDragOver
                            ? 'border-sage bg-sage/5 text-sage'
                            : 'border-beige text-gray-soft hover:border-sage hover:text-sage'
                        }`}
                        data-testid="feedback-add-image-btn"
                      >
                        <Camera size={16} />
                        {isDragOver ? 'Drop image here' : 'Add Screenshot or drag & drop'}
                      </button>
                    )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      data-testid="feedback-image-input"
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-beige flex-shrink-0">
                  <button
                    type="submit"
                    disabled={submitting || !message.trim()}
                    className="w-full py-3 bg-sage text-white rounded-lg font-medium hover:bg-sage-dark disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                    data-testid="feedback-submit-btn"
                  >
                    {submitting ? (
                      <><Loader2 size={18} className="animate-spin" /> Sending...</>
                    ) : (
                      'Send Feedback'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
    </div>
  );
};

export default FeedbackWidget;
